import { useEffect, useMemo, useState } from "react";
import { ExternalLink, MessageCircleMore } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@akuna/ui";
import {
  buildTermShareMessage,
  getTermSignedPublicUrl,
  shareTermViaWhatsApp,
  type TermOwnerPhone,
} from "@/lib/terms/sharing";
import { maskPhoneInput } from "@/lib/utils/phone-mask";
import type { PatientTerm } from "@/hooks/usePatientTerms";

export interface TermShareDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  term: PatientTerm & { digitally_signed_access_code?: string | null };
  ownerName?: string | null;
  ownerPhones?: TermOwnerPhone[];
  petName?: string | null;
}

export function TermShareDrawer({
  open,
  onOpenChange,
  term,
  ownerName,
  ownerPhones = [],
  petName,
}: TermShareDrawerProps) {
  const { t } = useTranslation("terms");
  const [selectedPhone, setSelectedPhone] = useState("");
  const [message, setMessage] = useState("");
  const [isPreparing, setIsPreparing] = useState(false);

  const signedPublicUrl = useMemo(() => getTermSignedPublicUrl(term), [term]);
  const validationUrl = "https://validar.iti.gov.br/";

  useEffect(() => {
    if (!open) return;
    const defaultPhone = maskPhoneInput(ownerPhones[0]?.phone_number ?? "");
    setSelectedPhone(defaultPhone);
    setMessage(buildTermShareMessage(petName, signedPublicUrl));
  }, [open, ownerPhones, petName, signedPublicUrl]);

  const handleSend = async () => {
    if (!selectedPhone) {
      toast.error(t("share.phoneRequired"));
      return;
    }

    setIsPreparing(true);
    try {
      const finalMessage = message.trim() || buildTermShareMessage(petName, signedPublicUrl);
      shareTermViaWhatsApp(selectedPhone, finalMessage);
      toast.success(t("share.sentSuccess"));
    } catch (error) {
      console.error(error);
      toast.error(t("share.sendError"));
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-hidden p-0 flex flex-col">
        <DialogHeader className="space-y-3 border-b border-border/70 px-6 py-5">
          <DialogTitle>{t("share.title")}</DialogTitle>
          <DialogDescription>{t("share.descriptionSigned")}</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-6 py-5">
          <div className="rounded-xl border border-border/70 bg-muted/30 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("share.summaryRecipient")}
            </p>
            <p className="mt-1 text-sm font-medium">
              {ownerName || t("share.missingOwner")}
            </p>
          </div>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="term-share-phone">{t("share.phoneLabel")}</Label>
              <Input
                id="term-share-phone"
                type="tel"
                list={ownerPhones.length > 0 ? "term-share-phone-suggestions" : undefined}
                value={selectedPhone}
                placeholder={t("share.phonePlaceholder")}
                onChange={(e) => setSelectedPhone(maskPhoneInput(e.target.value))}
              />
              {ownerPhones.length > 0 ? (
                <datalist id="term-share-phone-suggestions">
                  {ownerPhones.map((phone, index) => (
                    <option key={phone.phone_number + index} value={maskPhoneInput(phone.phone_number)} />
                  ))}
                </datalist>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("share.noWhatsappPhone")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="term-share-message">{t("share.messageLabel")}</Label>
              <Textarea
                id="term-share-message"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <div className="rounded-xl border border-border/70 bg-muted/20 p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-medium">{t("share.validationTitle")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("share.validationDescription")}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" asChild>
                  <a href={validationUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t("share.openValidation")}
                  </a>
                </Button>
                {signedPublicUrl && (
                  <Button type="button" variant="outline" size="sm" asChild>
                    <a href={signedPublicUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t("share.openSignedPdf")}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border/70 px-6 py-4">
          <Button type="button" onClick={handleSend} disabled={isPreparing}>
            <MessageCircleMore className="mr-2 h-4 w-4" />
            {isPreparing ? t("share.preparing") : t("share.sendNow")}
          </Button>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("share.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
